import React, { useEffect, useState } from "react";
import { X, Send, CheckCircle2, AlertCircle, Mail } from "lucide-react";
import {
  sendReportEmail,
  emailStatus,
  getEmailTemplate,
  putEmailTemplate,
  deleteEmailTemplate,
} from "../lib/api";
import {
  DEFAULT_EMAIL_SUBJECT,
  DEFAULT_EMAIL_BODY,
  DEFAULT_COMPARE_EMAIL_SUBJECT,
  DEFAULT_COMPARE_EMAIL_BODY,
  DEFAULT_COACHING_WEEK_EMAIL_SUBJECT,
  DEFAULT_COACHING_WEEK_EMAIL_BODY,
} from "../lib/constants";
import { toast } from "sonner";

// Legacy localStorage keys — kept ONLY for a one-time migration on first
// load. Once the server-side template is read or written, we delete the
// localStorage entry so it can never bleed back.
const LEGACY_TEMPLATE_KEY_TEST = "tir.emailTemplate.test.v3";
const LEGACY_TEMPLATE_KEY_COMPARE = "tir.emailTemplate.compare.v3";
const LEGACY_TEMPLATE_KEY_COACHING = "tir.emailTemplate.coachingWeek.v1";

function legacyKeyFor(reportType) {
  if (reportType === "compare") return LEGACY_TEMPLATE_KEY_COMPARE;
  if (reportType === "coaching_week") return LEGACY_TEMPLATE_KEY_COACHING;
  return LEGACY_TEMPLATE_KEY_TEST;
}

// Map our frontend `reportTypeLabel` to the backend `template_type` enum.
function templateTypeFor(reportType) {
  if (reportType === "compare") return "compare";
  if (reportType === "coaching_week") return "coaching_week";
  return "test";
}

function defaultsFor(reportType) {
  if (reportType === "compare")
    return { subject: DEFAULT_COMPARE_EMAIL_SUBJECT, body: DEFAULT_COMPARE_EMAIL_BODY };
  if (reportType === "coaching_week")
    return {
      subject: DEFAULT_COACHING_WEEK_EMAIL_SUBJECT,
      body: DEFAULT_COACHING_WEEK_EMAIL_BODY,
    };
  return { subject: DEFAULT_EMAIL_SUBJECT, body: DEFAULT_EMAIL_BODY };
}

// Read the legacy localStorage template (used only once for migration).
function readLegacyTemplate(reportType) {
  try {
    const raw = localStorage.getItem(legacyKeyFor(reportType));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function clearLegacyTemplate(reportType) {
  try {
    localStorage.removeItem(legacyKeyFor(reportType));
  } catch {
    /* ignore */
  }
}

function firstName(fullName) {
  return (fullName || "").trim().split(/\s+/)[0] || "there";
}

function fillTokens(text, athlete, testDate, extra = {}) {
  let out = (text || "")
    .replaceAll("{first_name}", firstName(athlete?.name))
    .replaceAll("{name}", athlete?.name || "")
    .replaceAll("{test_date}", testDate || "");
  for (const [k, v] of Object.entries(extra)) {
    out = out.replaceAll(`{${k}}`, v == null ? "" : String(v));
  }
  return out;
}

/**
 * Modal for emailing a generated report PDF to an athlete.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   athlete: { id, name, email }
 *   testId: string | null
 *   testDate: string (yyyy-mm-dd or pretty)
 *   compareTestIds: string[] | null
 *   defaultFilename: string
 *   generatePdf: () => Promise<Blob>     // caller-provided function that returns the PDF blob
 *   onSent: (record) => void              // called after successful send
 *   reportTypeLabel: "test" | "compare"   // for toasts/copy
 */
export default function EmailReportModal({
  open,
  onClose,
  athlete,
  testId = null,
  testDate = "",
  compareTestIds = null,
  defaultFilename,
  generatePdf,
  onSent,
  reportTypeLabel = "test",
  extraTokens = {},
}) {
  const fallback = defaultsFor(reportTypeLabel);
  const [recipient, setRecipient] = useState(athlete?.email || "");
  const [subject, setSubject] = useState(
    fillTokens(fallback.subject, athlete, testDate, extraTokens)
  );
  const [body, setBody] = useState(
    fillTokens(fallback.body, athlete, testDate, extraTokens)
  );
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // {configured, sender_email, ...}
  const [done, setDone] = useState(false);
  // Off by default — personalization for one athlete must NEVER overwrite
  // the default template silently. Coaches who want to evolve the
  // boilerplate explicitly tick this box before hitting Send.
  const [saveAsDefault, setSaveAsDefault] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setRecipient(athlete?.email || "");
    setDone(false);
    setSaveAsDefault(false);
    const fb = defaultsFor(reportTypeLabel);
    // Render defaults immediately so the modal isn't blank while we fetch.
    setSubject(fillTokens(fb.subject, athlete, testDate, extraTokens));
    setBody(fillTokens(fb.body, athlete, testDate, extraTokens));
    // Load the saved server-side template. If it doesn't exist, attempt a
    // one-time migration from the legacy localStorage entry (write to
    // server then wipe the localStorage so it can never bleed again).
    (async () => {
      let tpl = null;
      try {
        tpl = await getEmailTemplate(templateTypeFor(reportTypeLabel));
      } catch {
        tpl = null;
      }
      if (!tpl) {
        const legacy = readLegacyTemplate(reportTypeLabel);
        if (legacy && (legacy.subject || legacy.body)) {
          try {
            tpl = await putEmailTemplate(templateTypeFor(reportTypeLabel), {
              subject: legacy.subject || "",
              body: legacy.body || "",
            });
          } catch {
            /* ignore — fall back to defaults */
          }
        }
        // Whether migration succeeded or not, scrub the legacy entry so it
        // can never re-pollute the next session.
        clearLegacyTemplate(reportTypeLabel);
      }
      if (!alive) return;
      if (tpl?.subject || tpl?.body) {
        setSubject(fillTokens(tpl.subject || fb.subject, athlete, testDate, extraTokens));
        setBody(fillTokens(tpl.body || fb.body, athlete, testDate, extraTokens));
      }
    })();
    emailStatus()
      .then((s) => alive && setStatus(s))
      .catch(() => alive && setStatus({ configured: false }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, athlete, testDate, reportTypeLabel, JSON.stringify(extraTokens)]);

  if (!open) return null;

  const handleSend = async () => {
    if (!recipient.trim()) {
      toast.error("Recipient email is required");
      return;
    }
    if (!subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    setSending(true);
    try {
      const blob = await generatePdf();
      const base64 = await blobToBase64(blob);
      const filename = defaultFilename || `lactate-report-${testDate || "report"}.pdf`;

      // Save template for next time ONLY if the coach opted in. By default
      // edits are per-send personalisation that must NOT bleed into other
      // athletes' emails. Re-inject {test_date} so today's date doesn't bake
      // in, and re-inject {first_name} ONLY at the canonical "Hi <name>,"
      // greeting — replacing it anywhere else risks clobbering ordinary
      // words that happen to match an athlete's first name (e.g. an athlete
      // named "Test" would corrupt "Test Results" in the subject).
      if (saveAsDefault) {
        const fn = firstName(athlete?.name);
        const fnEsc = fn.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const greetingRegex = new RegExp(`^Hi ${fnEsc},`, "m");
        try {
          await putEmailTemplate(templateTypeFor(reportTypeLabel), {
            subject: testDate ? subject.replaceAll(testDate, "{test_date}") : subject,
            body: body.replace(greetingRegex, "Hi {first_name},"),
          });
          // Also wipe the legacy localStorage so an older browser session
          // can't re-pollute on next open.
          clearLegacyTemplate(reportTypeLabel);
        } catch {
          // Non-fatal — the email itself can still send.
          toast.error("Couldn't save template (the email is still on its way)");
        }
      }

      const result = await sendReportEmail({
        athlete_id: athlete.id,
        test_id: testId,
        compare_test_ids: compareTestIds,
        recipient_email: recipient.trim(),
        subject,
        body,
        attachments: [{ filename, content_base64: base64 }],
      });
      setDone(true);
      toast.success(`Sent to ${recipient.trim()}`);
      if (onSent) onSent(result);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Send failed — check email setup";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const notConfigured = status && status.configured === false;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="email-report-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl bg-[var(--tir-ivory)] border border-[var(--tir-ink)] shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between p-6 border-b border-[var(--tir-border)]">
          <div className="flex items-center gap-3">
            <Mail size={20} className="text-[var(--tir-burgundy)]" />
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold">
                Send report
              </div>
              <h3 className="font-serif-display text-2xl text-[var(--tir-ink)] leading-tight">
                Email this {reportTypeLabel === "compare" ? "comparison" : ""} report to {athlete?.name}
              </h3>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="email-modal-close"
            className="text-[var(--tir-muted)] hover:text-[var(--tir-ink)] p-1"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {done ? (
          <div className="p-10 text-center">
            <CheckCircle2
              size={56}
              className="mx-auto text-[var(--tir-burgundy)] mb-4"
              data-testid="email-sent-icon"
            />
            <div className="font-serif-display text-3xl text-[var(--tir-ink)] mb-2">
              Sent!
            </div>
            <p className="text-sm text-[var(--tir-muted)] mb-6">
              {athlete?.name}'s report is on its way to {recipient}.
            </p>
            <button
              onClick={onClose}
              data-testid="email-done-btn"
              className="bg-[var(--tir-burgundy)] text-white px-8 py-3 text-sm tracking-[0.22em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            {notConfigured && (
              <div
                className="flex items-start gap-3 border border-[var(--tir-burgundy)] bg-[var(--tir-burgundy)]/10 p-4 text-sm"
                data-testid="email-not-configured-warning"
              >
                <AlertCircle size={18} className="text-[var(--tir-burgundy)] shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-[var(--tir-burgundy)] mb-1">
                    Email isn't connected yet
                  </div>
                  <div className="text-[var(--tir-ink)] leading-relaxed">
                    Add your Resend API key to the backend before this will work. Once added, this banner disappears.
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                To
              </label>
              <input
                type="email"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                data-testid="email-to-input"
                placeholder="athlete@example.com"
                className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2.5 text-base focus:outline-none font-mono-tir"
              />
            </div>

            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="email-subject-input"
                className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2.5 text-base focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Message
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                data-testid="email-body-textarea"
                rows={11}
                className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2.5 text-sm focus:outline-none leading-relaxed resize-y"
              />
              <div className="flex items-center justify-between gap-3 mt-1.5">
                <div className="text-[10px] text-[var(--tir-muted)] leading-relaxed">
                  Replies will route to {status?.reply_to || "your inbox"}.
                  <br />
                  Tip: use <span className="font-mono-tir text-[var(--tir-ink)]">[click here](https://...)</span> to make any text into a clean clickable link.
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    // Server-side reset first (delete the saved template),
                    // then scrub localStorage for legacy entries, then
                    // restore the canonical default body.
                    try {
                      await deleteEmailTemplate(templateTypeFor(reportTypeLabel));
                    } catch {
                      /* non-fatal */
                    }
                    clearLegacyTemplate(reportTypeLabel);
                    const fb = defaultsFor(reportTypeLabel);
                    setSubject(fillTokens(fb.subject, athlete, testDate));
                    setBody(fillTokens(fb.body, athlete, testDate));
                    setSaveAsDefault(false);
                    toast.success("Reset to default template");
                  }}
                  data-testid="email-reset-template-btn"
                  className="shrink-0 text-[10px] tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors whitespace-nowrap"
                >
                  Reset to default
                </button>
              </div>
              <label
                className="mt-3 flex items-start gap-2 text-[11px] cursor-pointer select-none"
                data-testid="email-save-as-default-row"
              >
                <input
                  type="checkbox"
                  checked={saveAsDefault}
                  onChange={(e) => setSaveAsDefault(e.target.checked)}
                  data-testid="email-save-as-default-toggle"
                  className="mt-0.5 h-3.5 w-3.5 accent-[var(--tir-burgundy)]"
                />
                <span className="leading-snug text-[var(--tir-muted)]">
                  <span className="font-semibold tracking-[0.18em] uppercase text-[10px] text-[var(--tir-ink)]">
                    Save these edits as my default template
                  </span>
                  <span className="block italic">
                    Off by default so per-athlete tweaks stay per-athlete. Tick this only when you've improved the boilerplate you want every future email to start from.
                  </span>
                </span>
              </label>
            </div>

            <div className="flex items-center gap-2 text-xs text-[var(--tir-muted)] border-t border-[var(--tir-border)] pt-4">
              <span className="font-semibold tracking-[0.2em] uppercase text-[10px] text-[var(--tir-ink)]">
                Attachment
              </span>
              <span className="font-mono-tir" data-testid="email-attachment-name">
                {defaultFilename}
              </span>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2 border-t border-[var(--tir-border)]">
              <button
                type="button"
                onClick={onClose}
                data-testid="email-cancel-btn"
                className="text-sm tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)] px-4 py-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={sending || !recipient.trim()}
                data-testid="email-send-btn"
                className="inline-flex items-center gap-2 bg-[var(--tir-burgundy)] text-white px-6 py-2.5 text-sm tracking-[0.22em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors disabled:opacity-50"
              >
                <Send size={14} />
                {sending ? "Sending…" : "Send report"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      // result is a data URL "data:application/pdf;base64,XXXX". Strip the prefix.
      const idx = String(result).indexOf("base64,");
      resolve(idx >= 0 ? String(result).slice(idx + 7) : String(result));
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
